import { useEffect, useState } from 'react';
import { applyTheme, readStoredTheme, resolveTheme, type ThemePreference } from '@/lib/theme';

/**
 * Theme choice, three states rather than two: "follow my machine" is a real preference, and a
 * two-way switch silently discards it the first time anyone touches the control.
 */
export function useThemePreference() {
  const [preference, setPreference] = useState<ThemePreference>(readStoredTheme);

  useEffect(() => { applyTheme(preference); }, [preference]);

  // While on 'system', re-render when the OS flips so the "Auto (dark)" label stays truthful.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (preference !== 'system') return;
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = () => setTick(t => t + 1);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [preference]);

  return [preference, setPreference] as const;
}

const options: { value: ThemePreference; label: string }[] = [
  { value: 'system', label: 'Match my system' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' }
];

/** The theme choice as a radio group of menu items, for the account menu. */
export function ThemeMenuItems() {
  const [preference, setPreference] = useThemePreference();
  return (
    <div role="group" aria-label="Theme">
      <div className="menu-label">Theme</div>
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          role="menuitemradio"
          aria-checked={preference === o.value}
          className="menu-item"
          onClick={() => setPreference(o.value)}>
          {o.value === 'system' ? `${o.label} (${resolveTheme('system')})` : o.label}
          {preference === o.value && <span className="menu-check" aria-hidden="true">✓</span>}
        </button>
      ))}
    </div>
  );
}
